/**
 * CreateTeamDialog Component
 *
 * Full-featured dialog for creating a new team/department.
 * Matches the Organization page's Add Team functionality (name + description + manager).
 */

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Avatar,
    Autocomplete,
    CircularProgress,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Groups, Badge as BadgeIcon } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import useOrganizationSetupStore from '@/store/OrganizationSetupStore';
import { AvailableManager } from '@/services/OrganizationSetupService';

export const CreateTeamDialog: React.FC = () => {
    const __muiTheme = useTheme();
    const isMobile = useMediaQuery(__muiTheme.breakpoints.down("sm"));
    const { isDarkMode, data, dialogs } = useOKRDashboardContext();
    const { setSuccess, setError, refetchData } = data;
    const { dialogs: dialogState, closeCreateTeamDialog } = dialogs;

    // Local form state (same pattern as Organization page's TeamsSection)
    const [teamName, setTeamName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedManager, setSelectedManager] = useState<AvailableManager | null>(null);
    const [saving, setSaving] = useState(false);

    const {
        allEmployeesForManager,
        fetchAllEmployeesForManager,
        addTeam,
    } = useOrganizationSetupStore();

    // Fetch employees for manager dropdown when dialog opens
    useEffect(() => {
        if (dialogState.createTeamDialog) {
            fetchAllEmployeesForManager();
        }
    }, [dialogState.createTeamDialog, fetchAllEmployeesForManager]);

    const getManagerWarning = (employee: AvailableManager): string | null => {
        if (employee.manages_team_id) {
            return `Already manages: ${employee.manages_team_name}`;
        }
        return null;
    };

    const handleSave = async () => {
        if (!teamName.trim()) return;

        setSaving(true);
        try {
            const success = await addTeam({
                team_name: teamName,
                description: description || undefined,
                manager_id: selectedManager?.id || undefined,
            });

            if (success) {
                setSuccess('Team created successfully');
                handleClose();
                refetchData(); // Refresh OKR departments list
            } else {
                setError('Failed to create team');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to create team');
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        setTeamName('');
        setDescription('');
        setSelectedManager(null);
        closeCreateTeamDialog();
    };

    return (
        <Dialog
            open={dialogState.createTeamDialog}
            onClose={handleClose}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
                    borderRadius: 3,
                },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Groups sx={{ color: '#fff', fontSize: 24 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Create New Team
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <TextField
                        autoFocus
                        label="Team Name"
                        fullWidth
                        required
                        value={teamName}
                        onChange={(e) => setTeamName(e.target.value)}
                        placeholder="e.g., Engineering, Marketing, Sales"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            },
                        }}
                    />
                    <TextField
                        label="Description"
                        fullWidth
                        multiline
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Describe the team's responsibilities..."
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            },
                        }}
                    />
                    <Autocomplete
                        value={selectedManager}
                        onChange={(_, newValue) => setSelectedManager(newValue)}
                        options={allEmployeesForManager}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderOption={(props, option) => {
                            const warning = getManagerWarning(option);
                            const { key, ...rest } = props as any;
                            return (
                                <Box component="li" key={key} {...rest} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Avatar src={option.profile_photo} sx={{ width: 32, height: 32 }}>
                                        {option.name?.charAt(0)}
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2">{option.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {option.role_name} {option.current_department ? `- ${option.current_department}` : ''}
                                        </Typography>
                                        {warning && (
                                            <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                                                {warning}
                                            </Typography>
                                        )}
                                    </Box>
                                    {option.manages_team_id && (
                                        <BadgeIcon color="warning" fontSize="small" />
                                    )}
                                </Box>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Manager"
                                placeholder="Select a manager"
                                helperText="One employee can manage only one team"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    },
                                }}
                            />
                        )}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, pt: 1 }}>
                <Button
                    onClick={handleClose}
                    sx={{
                        color: isDarkMode ? '#94a3b8' : '#64748b',
                        fontWeight: 600,
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!teamName.trim() || saving}
                    sx={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 3,
                    }}
                >
                    {saving ? <CircularProgress size={24} color="inherit" /> : 'Create Team'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateTeamDialog;
